import "@/App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Building2, ClipboardCheck, FileClock, FileSpreadsheet, GraduationCap, Home, LayoutGrid, Mail, ScanLine, School, Users, Wallet } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, useSpaceName } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { HomeRedirect, RequireAuth } from "@/components/layout/Guards";
import LoginPage from "@/pages/LoginPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import NotFoundPage from "@/pages/NotFoundPage";
import PublicSchoolPage from "@/pages/PublicSchoolPage";
import KartuPublik from "@/pages/KartuPublik";
import PlatformSummary from "@/pages/platform/PlatformSummary";
import SchoolsList from "@/pages/platform/SchoolsList";
import SchoolCreate from "@/pages/platform/SchoolCreate";
import SchoolDetail from "@/pages/platform/SchoolDetail";
import IdentityReviews from "@/pages/platform/IdentityReviews";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import PlatformBilling from "@/pages/platform/PlatformBilling";
import SchoolHome from "@/pages/school/SchoolHome";
import SchoolProfile from "@/pages/school/SchoolProfile";
import SchoolMembers from "@/pages/school/SchoolMembers";
import SchoolInvitations from "@/pages/school/SchoolInvitations";
import SchoolAudit from "@/pages/school/SchoolAudit";
import AcademicPage from "@/pages/school/academic/AcademicPage";
import AttendancePage from "@/pages/school/attendance/AttendancePage";
import GradesPage from "@/pages/school/grades/GradesPage";

const PLATFORM_NAV = [
  { to: "/pusat-kendali", end: true, label: "Ringkasan", icon: LayoutGrid, testId: "nav-platform-summary" },
  { to: "/pusat-kendali/sekolah", label: "Sekolah", icon: Building2, testId: "nav-platform-schools" },
  { to: "/pusat-kendali/langganan", label: "Langganan", icon: Wallet, testId: "nav-platform-billing" },
  { to: "/pusat-kendali/tinjauan-identitas", label: "Tinjauan Identitas", icon: ClipboardCheck, testId: "nav-platform-reviews" },
  { to: "/pusat-kendali/audit", label: "Log Audit", icon: FileClock, testId: "nav-platform-audit" },
];

const SCHOOL_NAV = [
  { to: "/sekolah", end: true, label: "Beranda", icon: Home, testId: "nav-school-home", perms: ["profile:read"] },
  { to: "/sekolah/profil", label: "Profil Sekolah", icon: School, testId: "nav-school-profile", perms: ["profile:read"] },
  { to: "/sekolah/anggota", label: "Anggota", icon: Users, testId: "nav-school-members", perms: ["members:read"] },
  { to: "/sekolah/akademik", label: "Data Akademik", icon: GraduationCap, testId: "nav-school-academic", perms: ["academic:read"] },
  // Penilaian dibuka lewat izin grades:* bagi pengurus/guru; siswa dan orang tua masuk lewat peran untuk melihat rapor terbit.
  { to: "/sekolah/penilaian", label: "Penilaian & Rapor", icon: FileSpreadsheet, testId: "nav-school-grades", perms: ["grades:read", "grades:write", "grades:publish", "grades:settings"], selfRoles: ["siswa", "orang_tua"] },
  // Presensi dibuka untuk semua peran: pengurus memakai izin attendance:*, siswa/orang tua melihat rekap dirinya.
  { to: "/sekolah/presensi", label: "Presensi", icon: ScanLine, testId: "nav-school-attendance", perms: ["attendance:read", "attendance:take", "attendance:scan"], selfRoles: ["siswa", "orang_tua", "pegawai_non_asn"] },
  { to: "/sekolah/undangan", label: "Undangan", icon: Mail, testId: "nav-school-invitations", perms: ["invitations:read"] },
  { to: "/sekolah/audit", label: "Log Audit", icon: FileClock, testId: "nav-school-audit", perms: ["audit:read"] },
];

const SchoolLayout = () => {
  const { me } = useAuth();
  const space = useSpaceName();
  const nav = SCHOOL_NAV.filter((n) => n.perms.some((p) => me.school_permissions.includes(p)) || n.selfRoles?.includes(me.active_school?.role));
  return <AppShell variant="school" spaceLabel={space} nav={nav} />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/masuk" element={<LoginPage />} />
          <Route path="/undangan" element={<AcceptInvitePage />} />
          <Route path="/publik/:schoolId" element={<PublicSchoolPage />} />
          <Route path="/kartu/:kunci" element={<KartuPublik />} />
          <Route path="/pusat-kendali" element={<RequireAuth space="platform"><AppShell variant="platform" spaceLabel="Pusat Kendali" nav={PLATFORM_NAV} /></RequireAuth>}>
            <Route index element={<PlatformSummary />} />
            <Route path="sekolah" element={<SchoolsList />} />
            <Route path="sekolah/baru" element={<SchoolCreate />} />
            <Route path="sekolah/:id" element={<SchoolDetail />} />
            <Route path="langganan" element={<PlatformBilling />} />
            <Route path="tinjauan-identitas" element={<IdentityReviews />} />
            <Route path="audit" element={<PlatformAudit />} />
          </Route>
          <Route path="/sekolah" element={<RequireAuth space="school"><SchoolLayout /></RequireAuth>}>
            <Route index element={<SchoolHome />} />
            <Route path="profil" element={<SchoolProfile />} />
            <Route path="anggota" element={<SchoolMembers />} />
            <Route path="undangan" element={<SchoolInvitations />} />
            <Route path="audit" element={<SchoolAudit />} />
            <Route path="akademik" element={<AcademicPage />} />
            <Route path="penilaian" element={<GradesPage />} />
            <Route path="presensi" element={<AttendancePage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton containerAriaLabel="Notifikasi" toastOptions={{ closeButtonAriaLabel: "Tutup notifikasi" }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
